import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useEffect, useRef } from 'react'
import {
	Form,
	data,
	useActionData,
	useNavigation,
	useSearchParams,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { type Route } from './+types/contact.ts'

const ContactSchema = z.object({
	name: z.string().min(2, 'Nom trop court').max(120),
	email: z.string().email('Adresse courriel invalide'),
	organization: z.string().max(160).optional(),
	topic: z.enum(['demo', 'expert', 'other']),
	message: z.string().min(10, 'Message trop court').max(4000),
})

export const meta: Route.MetaFunction = () => [
	{ title: 'Contact — Vigilio' },
	{
		name: 'description',
		content:
			'Demandez une démo, parlez à un expert, posez vos questions. Réponse en moins de 24 h ouvrables.',
	},
]

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)

	const submission = parseWithZod(formData, { schema: ContactSchema })
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { name, email, organization, topic, message } = submission.value
	const topicLabel =
		topic === 'demo'
			? 'Démo'
			: topic === 'expert'
				? 'Parler à un expert'
				: 'Autre'

	try {
		await sendEmail({
			to: 'bonjour@vigilio.ca',
			replyTo: email,
			subject: `[Contact · ${topicLabel}] ${name}${organization ? ` — ${organization}` : ''}`,
			text: `Motif : ${topicLabel}
Nom : ${name}
Courriel : ${email}
Organisation : ${organization ?? '—'}

${message}`,
			html: `<p><strong>Motif :</strong> ${topicLabel}<br />
<strong>Nom :</strong> ${name}<br />
<strong>Courriel :</strong> ${email}<br />
<strong>Organisation :</strong> ${organization ?? '—'}</p>
<p>${message.replace(/\n/g, '<br />')}</p>`,
		})
	} catch (error) {
		console.error('[contact] sendEmail failed', error)
		return data(
			{
				result: submission.reply({
					formErrors: [
						"Envoi impossible pour le moment. Écrivez-nous directement à bonjour@vigilio.ca.",
					],
				}),
			},
			{ status: 500 },
		)
	}

	return data({ result: submission.reply({ resetForm: true }), sent: true })
}

export default function ContactPage() {
	const actionData = useActionData<typeof action>()
	const navigation = useNavigation()
	const isSubmitting = navigation.formAction === '/contact'
	const [searchParams] = useSearchParams()
	const topicParam = searchParams.get('topic')
	const defaultTopic: 'demo' | 'expert' | 'other' =
		topicParam === 'demo' || topicParam === 'expert' ? topicParam : 'other'

	const [form, fields] = useForm({
		id: 'contact-form',
		constraint: getZodConstraint(ContactSchema),
		lastResult: actionData?.result,
		defaultValue: { topic: defaultTopic },
		shouldValidate: 'onBlur',
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ContactSchema })
		},
	})

	const sent = Boolean(actionData && 'sent' in actionData && actionData.sent)
	const successRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		if (sent && successRef.current) {
			successRef.current.focus()
			successRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
		}
	}, [sent])

	return (
		<section
			className="relative overflow-hidden"
			style={{
				background: 'var(--ink)',
				color: 'white',
				padding: '180px 0 120px',
			}}
		>
			{/* Ambient gradient */}
			<div
				className="pointer-events-none absolute left-1/2 top-[-20%] -translate-x-1/2"
				style={{
					width: 900,
					height: 700,
					background:
						'radial-gradient(ellipse at center, rgba(93, 47, 240, 0.28) 0%, rgba(93, 47, 240, 0) 60%)',
				}}
			/>

			<div className="relative z-10 mx-auto max-w-[1240px] px-5 md:px-10">
				<div className="mx-auto mb-16 max-w-160 text-center">
					<div className="eyebrow eyebrow-dark mb-6 justify-center">
						Contact
					</div>
					<h1 className="pb-6 text-white">
						Parlons de votre <span className="serif-italic">veille</span>.
					</h1>
					<p
						className="mx-auto max-w-150"
						style={{
							fontSize: 18,
							color: 'rgba(255,255,255,0.7)',
							lineHeight: 1.6,
						}}
					>
						Démo de 20 minutes, questions sur un plan, besoins
						particuliers — un expert vous répond en moins de 24 h
						ouvrables.
					</p>
				</div>

				<div className="mx-auto grid max-w-[980px] gap-10 md:grid-cols-[1fr_1.3fr]">
					<aside className="space-y-8">
						<InfoBlock
							label="Écrire"
							value="bonjour@vigilio.ca"
							href="mailto:bonjour@vigilio.ca"
						/>
						<InfoBlock label="Horaires" value="Lun–Ven · 9h–17h HE" />
						<InfoBlock
							label="Hébergement"
							value="Canada · Données souveraines"
						/>
						<InfoBlock
							label="Langue"
							value="Support 100% en français"
						/>
					</aside>

					<div
						className="rounded-2xl p-8"
						style={{
							background: 'rgba(255,255,255,0.04)',
							border: '1px solid rgba(255,255,255,0.10)',
							backdropFilter: 'blur(10px)',
						}}
					>
						{sent ? (
							<div
								ref={successRef}
								tabIndex={-1}
								className="outline-none"
							>
								<div className="eyebrow eyebrow-dark mb-4">Envoyé</div>
								<h2 className="pb-3 text-white" style={{ fontSize: 28 }}>
									Message bien reçu.
								</h2>
								<p
									style={{
										fontSize: 16,
										color: 'rgba(255,255,255,0.7)',
										lineHeight: 1.6,
									}}
								>
									Un expert vous répondra à l'adresse indiquée en moins
									de 24 heures ouvrables.
								</p>
							</div>
						) : (
							<Form
								method="POST"
								className="space-y-5"
								{...getFormProps(form)}
							>
								<HoneypotInputs />

								<DarkField
									label="Nom complet"
									errors={fields.name.errors}
									inputProps={getInputProps(fields.name, {
										type: 'text',
									})}
									autoComplete="name"
								/>

								<DarkField
									label="Courriel"
									errors={fields.email.errors}
									inputProps={getInputProps(fields.email, {
										type: 'email',
									})}
									autoComplete="email"
								/>

								<DarkField
									label="Organisation (facultatif)"
									errors={fields.organization.errors}
									inputProps={getInputProps(fields.organization, {
										type: 'text',
									})}
									autoComplete="organization"
								/>

								<div>
									<DarkLabel>Motif</DarkLabel>
									<div className="grid grid-cols-3 gap-2">
										{(
											[
												{ value: 'demo', label: 'Démo' },
												{ value: 'expert', label: 'Parler à un expert' },
												{ value: 'other', label: 'Autre' },
											] as const
										).map((opt) => {
											const id = `${fields.topic.id}-${opt.value}`
											return (
												<label
													key={opt.value}
													htmlFor={id}
													className="flex cursor-pointer items-center justify-center rounded-xl px-2 py-3 text-[13px] transition-colors"
													style={{
														background:
															'rgba(255,255,255,0.04)',
														border:
															'1px solid rgba(255,255,255,0.15)',
														color: 'rgba(255,255,255,0.85)',
													}}
												>
													<input
														type="radio"
														id={id}
														name={fields.topic.name}
														value={opt.value}
														defaultChecked={
															defaultTopic === opt.value
														}
														className="peer sr-only"
													/>
													<span className="peer-checked:font-medium peer-checked:text-white">
														{opt.label}
													</span>
												</label>
											)
										})}
									</div>
								</div>

								<div>
									<DarkLabel>Message</DarkLabel>
									<textarea
										{...getInputProps(fields.message, {
											type: 'text',
										})}
										rows={5}
										className="w-full rounded-xl px-4 py-3 text-[14px] transition-colors outline-none"
										style={{
											background: 'rgba(255,255,255,0.04)',
											border: '1px solid rgba(255,255,255,0.15)',
											color: 'white',
											resize: 'vertical',
										}}
										placeholder="Contexte, équipe, volumes, délais…"
									/>
									{fields.message.errors ? (
										<DarkFieldError errors={fields.message.errors} />
									) : null}
								</div>

								{form.errors && form.errors.length > 0 ? (
									<div
										role="alert"
										className="rounded-lg px-3 py-2 text-[13px]"
										style={{
											background: 'rgba(209,67,67,0.1)',
											color: '#F8B4B4',
											border: '1px solid rgba(209,67,67,0.25)',
										}}
									>
										{form.errors.join(' ')}
									</div>
								) : null}

								<button
									type="submit"
									disabled={isSubmitting}
									className="inline-flex h-12 w-full items-center justify-center rounded-xl text-[15px] font-medium text-white transition-all hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60"
									style={{
										background: 'var(--brand)',
										boxShadow:
											'0 1px 0 rgba(255,255,255,0.15) inset, 0 8px 24px -8px var(--brand-glow)',
									}}
								>
									{isSubmitting ? 'Envoi…' : 'Envoyer le message'}
								</button>
							</Form>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}

function InfoBlock({
	label,
	value,
	href,
}: {
	label: string
	value: string
	href?: string
}) {
	const content = (
		<>
			<div
				className="mb-1.5 text-[11px] uppercase tracking-[0.12em]"
				style={{ color: 'rgba(255,255,255,0.4)' }}
			>
				{label}
			</div>
			<div
				className="text-[15px]"
				style={{ color: 'rgba(255,255,255,0.85)' }}
			>
				{value}
			</div>
		</>
	)
	return href ? (
		<a href={href} className="block transition-opacity hover:opacity-80">
			{content}
		</a>
	) : (
		<div>{content}</div>
	)
}

function DarkLabel({ children }: { children: React.ReactNode }) {
	return (
		<div
			className="mb-1.5 block text-[12px] tracking-[0.02em]"
			style={{ color: 'rgba(255,255,255,0.7)' }}
		>
			{children}
		</div>
	)
}

function DarkField({
	label,
	errors,
	inputProps,
	autoComplete,
}: {
	label: string
	errors?: Array<string>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	autoComplete?: string
}) {
	return (
		<div>
			<DarkLabel>{label}</DarkLabel>
			<input
				{...inputProps}
				autoComplete={autoComplete}
				className="w-full rounded-xl px-4 py-3 text-[14px] transition-colors outline-none"
				style={{
					background: 'rgba(255,255,255,0.04)',
					border: '1px solid rgba(255,255,255,0.15)',
					color: 'white',
				}}
			/>
			{errors && errors.length > 0 ? <DarkFieldError errors={errors} /> : null}
		</div>
	)
}

function DarkFieldError({ errors }: { errors: Array<string> }) {
	return (
		<div
			role="alert"
			className="mt-1.5 text-[12px]"
			style={{ color: '#F8B4B4' }}
		>
			{errors.join(' ')}
		</div>
	)
}
